import { filterClasses } from "@swivl-mono/helpers";
import { type BotPromptChannel, type UserStory, type UserStoryStatus, parseStoryNumber } from "@swivl-mono/types";

interface UserStoryRowProps {
  story: UserStory;
  isExpanded: boolean;
  isNew: boolean;
  onToggle: () => void;
  /** Optional bulk-select state. Omit to hide the checkbox column. */
  selectable?: { selected: boolean; onToggleSelect: () => void };
}

const CHANNEL_ORDER: BotPromptChannel[] = ["web", "sms", "voice"];

/**
 * One-line summary row in the list view. Click to expand/collapse the inline
 * detail panel below.
 *
 * Per-channel status (Phase 2): each declared channel renders its own status
 * pill instead of a single overall pill. Channels not in `channelStatus` are
 * omitted from the row. Last-eval results (when present in `story.lastEval`)
 * render as a small pass/fail dot adjacent to the channel pill.
 */
export function UserStoryRow({ story, isExpanded, isNew, onToggle, selectable }: UserStoryRowProps): JSX.Element {
  const handleClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    // Ignore clicks that originated inside the checkbox.
    if ((e.target as HTMLElement).closest("[data-bulk-checkbox]")) return;
    onToggle();
  };

  return (
    <div
      onClick={handleClick}
      className={filterClasses(
        "flex w-full cursor-pointer items-center gap-3 border-b border-grey-100 px-3 py-2 text-left hover:bg-grey-50",
        isExpanded && "bg-primary-50",
        selectable?.selected && "bg-primary-50"
      )}
    >
      {selectable && (
        <span data-bulk-checkbox onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={selectable.selected}
            onChange={selectable.onToggleSelect}
            className="cursor-pointer"
            aria-label={`Select ${story.id}`}
          />
        </span>
      )}
      <span className="w-14 shrink-0 rounded bg-grey-100 px-1.5 py-0.5 text-center font-mono text-xs font-semibold text-grey-700">
        US-{String(parseStoryNumber(story.id)).padStart(3, "0")}
      </span>
      <ChannelStatusPills story={story} />
      <span className="flex-grow truncate text-sm text-grey-800">{story.title}</span>
      <span className="text-xs text-grey-500">{story.category}</span>
      {story.eval && <span className="rounded bg-grey-100 px-1.5 py-0.5 text-[10px] text-grey-600 uppercase">eval</span>}
      {isNew && <span className="bg-primary-500 rounded px-1.5 py-0.5 text-[10px] text-white uppercase">new</span>}
      <span className="text-xs text-grey-400">{isExpanded ? "▾" : "▸"}</span>
    </div>
  );
}

function ChannelStatusPills({ story }: { story: UserStory }): JSX.Element {
  return (
    <span className="flex items-center gap-1">
      {CHANNEL_ORDER.map((c) => {
        const status = story.channelStatus[c];
        if (!status) return null;
        const last = story.lastEval?.[c];
        const tooltip = last
          ? `${c}: ${status} · last run #${last.runId} ${last.passed ? "passed" : "failed"} (${last.score}%) at ${new Date(last.ranAt).toLocaleString()}`
          : `${c}: ${status}`;
        return (
          <span key={c} className="flex items-center gap-0.5" title={tooltip}>
            <span className={filterClasses("rounded px-1.5 py-0.5 text-[10px] font-medium uppercase", pillClass(status))}>
              {c[0]}
              {statusEmoji(status)}
            </span>
            {last && (
              <span
                className={filterClasses("h-1.5 w-1.5 rounded-full", last.passed ? "bg-teal-500" : "bg-red-500")}
                aria-label={last.passed ? "last eval passed" : "last eval failed"}
              />
            )}
          </span>
        );
      })}
    </span>
  );
}

function pillClass(status: UserStoryStatus): string {
  if (status === "live") return "bg-green-100 text-green-700";
  if (status === "building") return "bg-yellow-100 text-yellow-700";
  if (status === "backlog") return "bg-red-100 text-red-700";
  return "bg-grey-100 text-grey-500";
}

function statusEmoji(status: UserStoryStatus): string {
  if (status === "live") return "✓";
  if (status === "building") return "…";
  if (status === "backlog") return "○";
  return "✗";
}
